import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["email", "firstName", "lastName"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useMemo, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { useUserContext } from "@magento/peregrine/lib/context/user";
import { useCartContext } from "../../context/cart";
import { useAwaitQuery } from "@magento/peregrine/lib/hooks/useAwaitQuery";
import { clearCartDataFromCache } from "@magento/peregrine/lib/Apollo/clearCartDataFromCache";
import { clearCustomerDataFromCache } from "@magento/peregrine/lib/Apollo/clearCustomerDataFromCache";
import { retrieveCartId } from "@magento/peregrine/lib/store/actions/cart";
import DEFAULT_OPERATIONS from "./createAccount.gql";
import { trackConsent } from "@Src_main/utils/useExponea";
import { CREATE_CUSTOMER_ADDRESS_MUTATION } from "@Pages/MyAccountData/customerInfo.js";
var consent_list_loyalty = {
  privacy_consent_partecipation: 3650
};
var consent_list_loyalty_profilation = {
  privacy_consent_profilation: 365,
  privacy_consent_communication: 3650
};
export var useCreateAccount = _ref => {
  var {
    initialValues = {},
    onSubmit = () => {},
    onCancel = () => {}
  } = _ref;
  var {
    createAccountMutation,
    createCartMutation,
    getCartDetailsQuery,
    getCustomerQuery,
    mergeCartsMutation,
    signInMutation
  } = DEFAULT_OPERATIONS;
  var apolloClient = useApolloClient();
  var [isSubmitting, setIsSubmitting] = useState(false);
  var [loading, setLoading] = useState(false);
  var [{
    cartId
  }, {
    createCart,
    removeCart,
    getCartDetails
  }] = useCartContext();
  var [{
    isGettingDetails,
    isSignedIn
  }, {
    getUserDetails,
    setToken
  }] = useUserContext();
  var [fetchCartId] = useMutation(createCartMutation);
  var [mergeCarts] = useMutation(mergeCartsMutation);

  // For create account and sign in mutations, we don't want to cache any
  // personally identifiable information (PII). So we set fetchPolicy to 'no-cache'.
  var [createAccount, {
    error: createAccountError
  }] = useMutation(createAccountMutation, {
    fetchPolicy: "no-cache"
  });
  var [createCustomerAddress, {
    error: createCustomerAddressError
  }] = useMutation(CREATE_CUSTOMER_ADDRESS_MUTATION, {
    fetchPolicy: "no-cache"
  });
  var [signIn, {
    error: signInError
  }] = useMutation(signInMutation, {
    fetchPolicy: "no-cache"
  });
  var fetchUserDetails = useAwaitQuery(getCustomerQuery);
  var fetchCartDetails = useAwaitQuery(getCartDetailsQuery);
  var handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);
  var handleSubmit = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(function* (formValues, date_of_birth) {
      setLoading(true);
      setIsSubmitting(true);
      try {
        // Create the account and then sign in.
        var {
          email,
          password,
          firstname,
          gender,
          profiling,
          address,
          postcode,
          city,
          telephone
        } = formValues;
        yield createAccount({
          variables: {
            email,
            password,
            firstname,
            date_of_birth,
            gender,
            has_accepted_loyalty: true,
            has_accepted_profiling: profiling ? profiling : false,
            has_accepted_privacy: false
          }
        });
        exponea.identify({
          account_id: email,
          email: email
        });
        for (var [consent, duration] of Object.entries(consent_list_loyalty)) {
          trackConsent(email, consent, duration);
        }
        if (profiling) {
          for (var [_consent, _duration] of Object.entries(consent_list_loyalty_profilation)) {
            trackConsent(email, _consent, _duration);
          }
        }

        // Get source cart id (guest cart id).
        var sourceCartId = cartId;
        var signInResponse = yield signIn({
          variables: {
            email: email,
            password: password
          }
        });
        var token = signInResponse.data.generateCustomerToken.token;
        yield createCustomerAddress({
          variables: {
            address: address !== null && address !== void 0 && address.length ? address : ["anon"],
            postcode: postcode || "anon",
            city: city || "anon",
            firstname: firstname || "anon",
            lastname: "anon",
            telephone: telephone || "anon"
          },
          context: {
            headers: {
              authorization: "Bearer ".concat(token)
            }
          }
        });
        yield setToken(token);

        // Clear all cart/customer data from cache and redux.
        yield clearCartDataFromCache(apolloClient);
        yield clearCustomerDataFromCache(apolloClient);
        yield removeCart();

        // Create and get the customer's cart id.
        yield createCart({
          fetchCartId
        });
        var destinationCartId = yield retrieveCartId();

        // Merge the guest cart into the customer cart.
        yield mergeCarts({
          variables: {
            destinationCartId,
            sourceCartId
          }
        });

        // Ensure old stores are updated with any new data.
        yield getUserDetails({
          fetchUserDetails
        });
        yield getCartDetails({
          fetchCartId,
          fetchCartDetails
        });

        // Finally, invoke the post-submission callback.
        if (onSubmit) {
          onSubmit();
          setLoading(false);
        }
        setLoading(false);
      } catch (error) {
        console.error(error);
        setIsSubmitting(false);
        setLoading(false);
      }
    });
    return function (_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }(), [cartId, apolloClient, removeCart, createAccount, signIn, setToken, createCart, fetchCartId, mergeCarts, getUserDetails, fetchUserDetails, getCartDetails, fetchCartDetails, onSubmit]);
  var sanitizedInitialValues = useMemo(() => {
    var {
        email,
        firstName,
        lastName
      } = initialValues,
      rest = _objectWithoutProperties(initialValues, _excluded);
    return _objectSpread({
      customer: {
        email,
        firstname: firstName,
        lastname: lastName
      }
    }, rest);
  }, [initialValues]);
  var errors = useMemo(() => new Map([["createAccountQuery", createAccountError], ["signInMutation", signInError]]), [createAccountError, signInError]);
  return {
    errors,
    handleCancel,
    handleSubmit,
    initialValues: sanitizedInitialValues,
    isDisabled: isSubmitting || isGettingDetails,
    isSignedIn,
    loading
  };
};